import type { ProductModel } from "./ProductModel";

export const PRODUCT_GRAYSCALE_ASSET_KEYS = [
  "imageUrl",
  "maskUrl",
  "overlayPatternMaskUrl",
  "customLabelInstructionMaskUrl",
] as const;
export const PRODUCT_RGBA_ASSET_KEYS = ["highlightUrl"] as const;

export type ProductGrayscaleAssetKey =
  (typeof PRODUCT_GRAYSCALE_ASSET_KEYS)[number];
export type ProductRGBAAssetKey = (typeof PRODUCT_RGBA_ASSET_KEYS)[number];
export type ProductAssetKey = ProductGrayscaleAssetKey | ProductRGBAAssetKey;

export type SpecsModelAssetPath = Array<string | number>;

const PRODUCT_GRAYSCALE_ASSET_KEY_SET = new Set<string>(
  PRODUCT_GRAYSCALE_ASSET_KEYS
);
const PRODUCT_RGBA_ASSET_KEY_SET = new Set<string>(PRODUCT_RGBA_ASSET_KEYS);

export function isSpecsRecord(
  value: unknown
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function collectAssetPathsFromValue(
  value: unknown,
  currentPath: SpecsModelAssetPath,
  paths: SpecsModelAssetPath[],
  assetKeySet: Set<string>
) {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      collectAssetPathsFromValue(
        item,
        [...currentPath, index],
        paths,
        assetKeySet
      );
    });
    return;
  }

  if (!isSpecsRecord(value)) {
    return;
  }

  for (const [key, childValue] of Object.entries(value)) {
    const childPath = [...currentPath, key];
    if (
      assetKeySet.has(key) &&
      typeof childValue === "string" &&
      childValue.trim()
    ) {
      paths.push(childPath);
    }

    collectAssetPathsFromValue(childValue, childPath, paths, assetKeySet);
  }
}

export function collectProductAssetPathsFromValue(
  value: unknown,
  assetKeys: readonly ProductAssetKey[],
  currentPath: SpecsModelAssetPath = []
) {
  const paths: SpecsModelAssetPath[] = [];
  collectAssetPathsFromValue(value, currentPath, paths, new Set(assetKeys));
  return paths;
}

export function collectProductGrayscaleAssetPaths(
  product: ProductModel
): SpecsModelAssetPath[] {
  return collectProductAssetPathsFromValue(
    product.save(),
    PRODUCT_GRAYSCALE_ASSET_KEYS
  );
}

export function collectProductRGBAAssetPaths(
  product: ProductModel
): SpecsModelAssetPath[] {
  return collectProductAssetPathsFromValue(
    product.save(),
    PRODUCT_RGBA_ASSET_KEYS
  );
}
